/**
 * LLM 使用量のコスト計算
 *
 * model-registry.ts の pricing 情報を使用してコストを計算する。
 */
import { getModelById } from '../registry/model-registry';
/**
 * 入力トークン数に応じて適用する単価を返す。
 * 長文脈料金があり、入力（キャッシュ読込・書込分を含む合計）が閾値を超える場合はリクエスト全体に長文脈単価を適用する。
 */
export function selectPricing(pricing, inputTokens) {
    const long = pricing.longContext;
    if (!long || inputTokens <= long.thresholdInputTokens) {
        return pricing;
    }
    return {
        inputTokens: long.inputTokens,
        outputTokens: long.outputTokens,
        cachedTokens: long.cachedTokens,
        cacheWriteTokens: long.cacheWriteTokens,
        reasoningTokens: long.reasoningTokens,
    };
}
/**
 * キャッシュ読込・書込を除いた入力トークン数を返す。
 * inputTokens はキャッシュ分を含む合計のため、内訳として並べる・通常入力単価を掛けるときはこの値を使う。
 */
export function getNonCachedInputTokens(usage) {
    return Math.max(0, usage.inputTokens - (usage.cacheReadTokens ?? 0) - (usage.cacheWriteTokens ?? 0));
}
/**
 * 推論分を除いた出力トークン数を返す。
 * outputTokens は推論分を含む合計のため、内訳として並べる・通常出力単価を掛けるときはこの値を使う。
 */
export function getNonReasoningOutputTokens(usage) {
    return Math.max(0, usage.outputTokens - (usage.reasoningTokens ?? 0));
}
/**
 * 合計トークン数を返す（欠けている値は 0 として扱う）。
 * 入力はキャッシュ分、出力は推論分を含む合計のため、キャッシュ・推論は足さない。
 */
export function getTotalTokens(usage) {
    return (usage.inputTokens ?? 0) + (usage.outputTokens ?? 0);
}
/**
 * 保存値（入力はキャッシュ込み、出力は推論込みの合計）から、画面に並べる内訳と合計を求める（欠けている値は 0 として扱う）
 */
export function getUsageBreakdown(usage) {
    const normalized = {
        inputTokens: usage.inputTokens ?? 0,
        outputTokens: usage.outputTokens ?? 0,
        reasoningTokens: usage.reasoningTokens ?? 0,
        cacheReadTokens: usage.cacheReadTokens ?? 0,
        cacheWriteTokens: usage.cacheWriteTokens ?? 0,
    };
    return {
        input: getNonCachedInputTokens(normalized),
        output: getNonReasoningOutputTokens(normalized),
        reasoning: normalized.reasoningTokens ?? 0,
        cacheRead: normalized.cacheReadTokens ?? 0,
        cacheWrite: normalized.cacheWriteTokens ?? 0,
        total: getTotalTokens(normalized),
    };
}
/**
 * 内訳が合計を上回る不整合な使用量を警告する（計算では 0 に丸めるため、原因を見落とさないようにする）
 */
function warnInconsistentUsage(provider, modelId, usage) {
    const cacheTokens = (usage.cacheReadTokens ?? 0) + (usage.cacheWriteTokens ?? 0);
    if (cacheTokens > usage.inputTokens) {
        console.warn(`[cost-calculator] cache tokens exceed inputTokens: ${provider}:${modelId}`, usage);
    }
    if ((usage.reasoningTokens ?? 0) > usage.outputTokens) {
        console.warn(`[cost-calculator] reasoningTokens exceed outputTokens: ${provider}:${modelId}`, usage);
    }
}
/**
 * 使用量からコストを計算
 * @returns コスト情報、不明なモデルの場合は undefined
 */
export function calculateCost(provider, modelId, usage) {
    // model-registry から pricing 情報を取得
    const model = getModelById(modelId, provider);
    if (!model?.pricing) {
        console.warn(`[cost-calculator] Unknown model or no pricing: ${provider}:${modelId}`);
        return undefined;
    }
    warnInconsistentUsage(provider, modelId, usage);
    const pricing = selectPricing(model.pricing, usage.inputTokens);
    // 各トークンタイプのコストを計算（per 1M tokens → per token に変換）
    // 通常入力単価はキャッシュ以外の分に、通常出力単価は推論以外の分にだけ掛ける
    const inputCost = (getNonCachedInputTokens(usage) * pricing.inputTokens) / 1_000_000;
    const outputCost = (getNonReasoningOutputTokens(usage) * pricing.outputTokens) / 1_000_000;
    // オプションのトークンタイプ
    const cacheReadCost = usage.cacheReadTokens
        ? (usage.cacheReadTokens * (pricing.cachedTokens ?? pricing.inputTokens)) / 1_000_000
        : undefined;
    const cacheWriteCost = usage.cacheWriteTokens
        ? (usage.cacheWriteTokens * (pricing.cacheWriteTokens ?? pricing.inputTokens * 1.25)) /
            1_000_000
        : undefined;
    const reasoningCost = usage.reasoningTokens
        ? (usage.reasoningTokens * (pricing.reasoningTokens ?? pricing.outputTokens)) / 1_000_000
        : undefined;
    const total = inputCost + outputCost + (cacheReadCost ?? 0) + (cacheWriteCost ?? 0) + (reasoningCost ?? 0);
    return {
        total,
        input: inputCost,
        output: outputCost,
        reasoning: reasoningCost,
        cacheRead: cacheReadCost,
        cacheWrite: cacheWriteCost,
    };
}
//# sourceMappingURL=cost-calculator.js.map